import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useLanguage, LANGUAGES } from "../LanguageContext";

export default function Navbar() {
  const { pathname } = useLocation();
  const { lang, setLang, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [cycleIdx, setCycleIdx] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  const hideTimeout = useRef(null);

  // Smooth cycling: fade out → swap text → fade in
  useEffect(() => {
    const id = setInterval(() => {
      setTextVisible(false);
      setTimeout(() => {
        setCycleIdx((i) => (i + 1) % LANGUAGES.length);
        setTextVisible(true);
      }, 500);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => () => clearTimeout(hideTimeout.current), []);

  const handleLangEnter = () => {
    clearTimeout(hideTimeout.current);
    setLangOpen(true);
  };
  const handleLangLeave = () => {
    hideTimeout.current = setTimeout(() => setLangOpen(false), 1500);
  };

  const navLinks = [
    { to: "/",          label: t.nav.home },
    { to: "/voice",     label: t.nav.voiceAnalysis },
    { to: "/skin",      label: t.nav.skinAnalysis },
    { to: "/dashboard", label: t.nav.dashboard },
  ];

  return (
    <nav className="bg-white border-b border-stone-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-700 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <span className="font-bold text-lg text-stone-900 tracking-tight">WorryLens</span>
            <span className="hidden sm:inline text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
              Beta
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  pathname === to
                    ? "bg-amber-50 text-amber-800"
                    : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
                }`}
              >
                {label}
              </Link>
            ))}

            {/* Language selector */}
            <div
              className="relative ml-1"
              onMouseEnter={handleLangEnter}
              onMouseLeave={handleLangLeave}
            >
              <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-50 transition-colors">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                {/* Fixed-width container prevents layout shift between words */}
                <span
                  style={{
                    display: "inline-block",
                    width: "76px",
                    textAlign: "left",
                    opacity: textVisible ? 1 : 0,
                    transition: "opacity 0.5s ease-in-out",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {LANGUAGES[cycleIdx].langWord}
                </span>
                <svg className="w-3 h-3 text-stone-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {langOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg py-1 min-w-[150px] z-50 animate-fade-in">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center justify-between gap-3 ${
                        lang === l.code
                          ? "text-amber-700 bg-amber-50 font-semibold"
                          : "text-stone-700 hover:bg-stone-50"
                      }`}
                    >
                      <span>{l.label}</span>
                      {lang === l.code && (
                        <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-stone-500 hover:bg-stone-100"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden pb-3 border-t border-stone-100 mt-1 pt-2 animate-fade-in">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMenuOpen(false)}
                className={`block px-3 py-2 rounded-lg text-sm font-medium mb-1 transition-colors ${
                  pathname === to
                    ? "bg-amber-50 text-amber-800"
                    : "text-stone-600 hover:bg-stone-50"
                }`}
              >
                {label}
              </Link>
            ))}
            <div className="border-t border-stone-100 mt-2 pt-2">
              <p className="px-3 py-1 text-xs text-stone-400 font-medium uppercase tracking-wide flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                {LANGUAGES[cycleIdx].langWord}
              </p>
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setMenuOpen(false); }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium mb-1 transition-colors flex items-center justify-between ${
                    lang === l.code
                      ? "bg-amber-50 text-amber-800 font-semibold"
                      : "text-stone-600 hover:bg-stone-50"
                  }`}
                >
                  {l.label}
                  {lang === l.code && (
                    <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
